import * as React from "react";
import { createPortal } from "react-dom";
import { Icon, TYPE_VISUAL } from "./icons";
import { fetchContactThumbnail, ThumbnailSize } from "./api/graphPhoto";
import { useSession } from "./SessionContext";
import type { ContactEntity, Entity } from "./types";

/**
 * Tiny hover tooltip with a configurable delay. Rendered via portal so
 * the parent card's `overflow: hidden` doesn't clip it. Native HTML
 * `title=` would also work but its delay is browser-controlled
 * (typically ~500-1000 ms) and we want something snappier.
 */
function useHoverTooltip(text: string, delayMs = 100) {
  const [visible, setVisible] = React.useState(false);
  const [pos, setPos] = React.useState({ left: 0, top: 0 });
  const timerRef = React.useRef<number | null>(null);

  const clearTimer = () => {
    if (timerRef.current != null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  React.useEffect(() => () => clearTimer(), []);

  const onMouseEnter = (e: React.MouseEvent<HTMLElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setPos({ left: rect.left + rect.width / 2, top: rect.bottom + 6 });
    clearTimer();
    timerRef.current = window.setTimeout(() => setVisible(true), delayMs);
  };
  const onMouseLeave = () => {
    clearTimer();
    setVisible(false);
  };

  const node = visible
    ? createPortal(
        <div
          className="hover-tooltip"
          style={{ left: pos.left, top: pos.top }}
          role="tooltip"
        >
          {text}
        </div>,
        document.body
      )
    : null;

  return { handlers: { onMouseEnter, onMouseLeave }, node };
}

export const cls = (...xs: (string | false | undefined | null)[]): string =>
  xs.filter(Boolean).join(" ");

export function Avatar({
  contact,
  size = "md",
}: {
  contact: ContactEntity;
  size?: "md" | "lg" | "xl";
}): JSX.Element {
  const sizeClass = size === "xl" ? "avatar-xl" : size === "lg" ? "avatar-lg" : "avatar-md";
  const thumbSize: ThumbnailSize = size === "xl" ? "medium" : "small";
  const photoUrl = useContactHeadshotUrl(contact, thumbSize);
  const ratingClass =
    contact.rating && contact.rating !== "NA"
      ? `rating-${contact.rating.toLowerCase()}`
      : null;

  // Track when the <img>'s bytes have actually arrived. Until then we keep
  // the initials monogram visible so the avatar isn't blank during the
  // CDN fetch (~100–500 ms even on cache hit). Reset whenever the URL
  // changes (e.g. switching to a different contact).
  const [imgLoaded, setImgLoaded] = React.useState(false);
  React.useEffect(() => {
    setImgLoaded(false);
  }, [photoUrl]);
  const photoReady = !!photoUrl && imgLoaded;

  return (
    <div className="av-wrap">
      <div className={cls(sizeClass, !photoReady && `av-${contact.palette}`, photoReady && "avatar-photo")}>
        {photoUrl && (
          <img
            src={photoUrl}
            alt=""
            className={cls("av-img", imgLoaded && "loaded")}
            onLoad={() => setImgLoaded(true)}
          />
        )}
        {!photoReady && contact.initials}
      </div>
      {ratingClass && (
        <div
          className={`rating-badge ${ratingClass}`}
          title={`Rating: ${contact.rating!.toLowerCase()}`}
        />
      )}
    </div>
  );
}

/**
 * Resolve a SharePoint headshot thumbnail URL for the given contact, or
 * null while loading / on failure / when the contact has no headshot.
 *
 * The pre-signed Graph CDN URL lives for ~60 min and our cache holds it
 * for 45 min, so the typical render is a synchronous cache hit; first
 * load per contact is one Graph round-trip.
 */
function useContactHeadshotUrl(
  contact: ContactEntity,
  size: ThumbnailSize
): string | null {
  const session = useSession();
  const graphToken = session?.graphAccessToken ?? null;
  const driveId = contact.headshotDriveId;
  const itemId = contact.headshotItemId;
  const [url, setUrl] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!driveId || !itemId || !graphToken) {
      setUrl(null);
      return;
    }
    let cancelled = false;
    fetchContactThumbnail(driveId, itemId, size, graphToken).then((u) => {
      if (!cancelled) setUrl(u);
    });
    return () => {
      cancelled = true;
    };
  }, [driveId, itemId, size, graphToken]);

  return url;
}

export function OutlookHeader({
  leadingKind = "none",
  title,
  subtitle,
  onLeading,
  trailing,
}: {
  leadingKind?: "close" | "back" | "none";
  title: string;
  subtitle?: string;
  onLeading?: () => void;
  trailing?: React.ReactNode;
}): JSX.Element {
  return (
    <div className="outlook-header">
      {leadingKind !== "none" && (
        <button className="leading" onClick={onLeading} aria-label={leadingKind}>
          {leadingKind === "back" ? <Icon.Back /> : <Icon.Close />}
        </button>
      )}
      <div className="title-stack">
        <div>{title}</div>
        {subtitle && <div className="small">{subtitle}</div>}
      </div>
      {trailing}
    </div>
  );
}

export function SectionLabel({ title, count }: { title: string; count?: string }): JSX.Element {
  return (
    <div className="section-label">
      <span>{title}</span>
      {count && <span className="count">{count}</span>}
    </div>
  );
}

export function EntityRow({
  entity,
  alreadyLinked,
  selected,
  justAdded,
  trailing,
  onClick,
}: {
  entity: Entity;
  alreadyLinked?: boolean;
  selected?: boolean;
  justAdded?: boolean;
  trailing?: React.ReactNode;
  onClick?: () => void;
}): JSX.Element {
  const meta = entity.meta;
  const isOverdue = entity.kind === "task" && entity.overdue;
  const ref = entity.ref;
  const isCaseClosed = entity.kind === "case" && entity.status === "closed";
  const tooltip = useHoverTooltip(entity.title, 100);

  return (
    <>
    <button
      className={cls("row", selected && "selected", justAdded && "just-added")}
      onClick={onClick}
      type="button"
      {...tooltip.handlers}
    >
      <span className="type-emoji" aria-hidden="true">
        {entity.emoji ?? TYPE_VISUAL[entity.kind].emoji}
      </span>
      <div className="row-info">
        <div className="row-title">{entity.title}</div>
        <div className="row-meta">
          {ref && <span className="ref">{ref}</span>}
          {isCaseClosed && <span className="status-pill closed">Closed</span>}
          {meta && (
            <>
              {(ref || isCaseClosed) && <span className="dot" />}
              <span className={cls("ellipsis", isOverdue && "urgent")}>{meta}</span>
            </>
          )}
        </div>
      </div>
      {trailing ?? (
        alreadyLinked ? (
          <span className="already"><Icon.Check color="currentColor" />LINKED</span>
        ) : (
          <span className="chev"><Icon.Chevron /></span>
        )
      )}
    </button>
    {tooltip.node}
    </>
  );
}

export function Toast({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <div className="toast">
      <div className="check"><Icon.Check /></div>
      {children}
    </div>
  );
}

/**
 * Modal confirm dialog — dims the pane and asks before a costly or
 * easily-mis-tapped action. Portaled to <body> so an ancestor's
 * transform/overflow can't clip or mis-position the fixed backdrop.
 * Backdrop-tap and Esc cancel, but both are disabled while `busy` so
 * the dialog can't be dismissed mid-request; the primary button swaps
 * to `busyLabel` and everything locks while the action is in flight.
 */
export function ConfirmSheet({
  title,
  body,
  confirmLabel,
  busyLabel,
  cancelLabel = "Cancel",
  busy = false,
  onConfirm,
  onCancel,
}: {
  title: string;
  body: React.ReactNode;
  confirmLabel: string;
  busyLabel?: string;
  cancelLabel?: string;
  busy?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}): JSX.Element {
  // Esc cancels, but never mid-request.
  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !busy) onCancel();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [busy, onCancel]);

  return createPortal(
    <div className="sheet-backdrop" onClick={() => !busy && onCancel()}>
      <div
        className="confirm-sheet"
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="confirm-title">{title}</div>
        <div className="confirm-body">{body}</div>
        <div className="confirm-actions">
          <button className="btn secondary" onClick={onCancel} disabled={busy}>
            {cancelLabel}
          </button>
          <button
            className={cls("btn primary", busy && "disabled")}
            onClick={onConfirm}
            disabled={busy}
          >
            {busy ? (
              <span className="confirm-busy">
                <span className="confirm-spinner" aria-hidden="true" />
                {busyLabel ?? confirmLabel}
              </span>
            ) : (
              confirmLabel
            )}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

export function RecipientRow({
  contact,
  role,
  onClick,
}: {
  contact: ContactEntity;
  role: "FROM" | "TO" | "CC";
  onClick?: () => void;
}): JSX.Element {
  return (
    <button className="recip-row" onClick={onClick}>
      <Avatar contact={contact} size="xl" />
      <div className="recip-info">
        <div className="recip-name">
          <span className="recip-name-text">{contact.title}</span>
          <span className={cls("role-tag", role.toLowerCase())}>{role}</span>
        </div>
        {contact.role && <div className="recip-detail">{contact.role}</div>}
        {contact.company && <div className="recip-detail">{contact.company}</div>}
      </div>
      <span className="chev"><Icon.Chevron /></span>
    </button>
  );
}

export function BottomBar({
  primaryLabel,
  primaryDisabled,
  primaryOnClick,
  secondaryLabel,
  secondaryOnClick,
}: {
  primaryLabel: string;
  primaryDisabled?: boolean;
  primaryOnClick: () => void;
  secondaryLabel: string;
  secondaryOnClick: () => void;
}): JSX.Element {
  return (
    <div className="bottom-bar">
      <button className="btn secondary" onClick={secondaryOnClick}>
        {secondaryLabel}
      </button>
      <button
        className={cls("btn primary", primaryDisabled && "disabled")}
        disabled={primaryDisabled}
        onClick={primaryOnClick}
      >
        {primaryLabel}
      </button>
    </div>
  );
}
